import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import AppShell from './components/AppShell';
import { useKaannos } from './lib/i18n';
import { VaatiiKirjautumisen, VaatiiOikeuden } from './lib/session';
import KalenteriSivu from './routes/KalenteriSivu';
import KirjauduSivu from './routes/KirjauduSivu';
import EiLoytynytSivu from './routes/EiLoytynytSivu';

/**
 * Kalenteri ja kirjautuminen ladataan heti — ne ovat se, mitä koko talo
 * avaa joka aamu. Raportit, vienti ja hallinta ladataan vasta tarvittaessa:
 * useimmat käyttäjät eivät avaa niitä koskaan, eikä heidän tarvitse ladata
 * niitä joka kerta.
 */
const OmatTunnitSivu = lazy(() => import('./routes/OmatTunnitSivu'));
const AgenttiSivu = lazy(() => import('./routes/AgenttiSivu'));
const KoulutuksetSivu = lazy(() => import('./routes/KoulutuksetSivu'));
const KoulutusSivu = lazy(() => import('./routes/KoulutusSivu'));
const KoulutusOsioSivu = lazy(() => import('./routes/KoulutusOsioSivu'));
const KoulutusTenttiSivu = lazy(() => import('./routes/KoulutusTenttiSivu'));
const KoulutusTulosSivu = lazy(() => import('./routes/KoulutusTulosSivu'));
const KehitysSivu = lazy(() => import('./routes/KehitysSivu'));
const KehitysHenkiloSivu = lazy(() => import('./routes/KehitysHenkiloSivu'));
const KehitysPolutSivu = lazy(() => import('./routes/KehitysPolutSivu'));
const KehitysPolkuSivu = lazy(() => import('./routes/KehitysPolkuSivu'));
const KeskusteluSivu = lazy(() => import('./routes/KeskusteluSivu'));
const RaportitSivu = lazy(() => import('./routes/RaportitSivu'));
const RaporttiAsiakasSivu = lazy(() => import('./routes/RaporttiAsiakasSivu'));
const VientiSivu = lazy(() => import('./routes/VientiSivu'));
const TaulutSivu = lazy(() => import('./routes/TaulutSivu'));
const TauluSivu = lazy(() => import('./routes/TauluSivu'));
const TehtavaLinkkiSivu = lazy(() => import('./routes/TehtavaLinkkiSivu'));
const OmatTehtavatSivu = lazy(() => import('./routes/OmatTehtavatSivu'));
const UimarataRaporttiSivu = lazy(() => import('./routes/UimarataRaporttiSivu'));
const GanttSivu = lazy(() => import('./routes/GanttSivu'));
const JumissaSivu = lazy(() => import('./routes/JumissaSivu'));
const KumppanitSivu = lazy(() => import('./routes/KumppanitSivu'));
const UlkoasuSivu = lazy(() => import('./routes/UlkoasuSivu'));
const HallintaKehys = lazy(() => import('./routes/hallinta/HallintaKehys'));
const AsiakkaatSivu = lazy(() => import('./routes/hallinta/AsiakkaatSivu'));
const AsiakasSivu = lazy(() => import('./routes/hallinta/AsiakasSivu'));
const ProjektitSivu = lazy(() => import('./routes/hallinta/ProjektitSivu'));
const ProjektiSivu = lazy(() => import('./routes/hallinta/ProjektiSivu'));
const KayttajatSivu = lazy(() => import('./routes/hallinta/KayttajatSivu'));
const KuukaudetSivu = lazy(() => import('./routes/hallinta/KuukaudetSivu'));
const HinnastoSivu = lazy(() => import('./routes/hallinta/HinnastoSivu'));
const AttribuutitSivu = lazy(() => import('./routes/hallinta/AttribuutitSivu'));
const MuutoslokiSivu = lazy(() => import('./routes/hallinta/MuutoslokiSivu'));
const AsetuksetSivu = lazy(() => import('./routes/hallinta/AsetuksetSivu'));
const HallintaKoulutuksetSivu = lazy(() => import('./routes/hallinta/KoulutuksetSivu'));
const KoulutusRaporttiSivu = lazy(() => import('./routes/hallinta/KoulutusRaporttiSivu'));
const OsaaminenSivu = lazy(() => import('./routes/hallinta/OsaaminenSivu'));
const KehitysKatsausSivu = lazy(() => import('./routes/hallinta/KehitysKatsausSivu'));
const SalaisuudetSivu = lazy(() => import('./routes/SalaisuudetSivu'));
/**
 * Vastaanottajan sivu omana palanaan: se on ainoa, jonka asiakas lataa,
 * eikä sen tarvitse tuoda mukanaan sovelluksen muita näkymiä.
 */
const SalaisuusSivu = lazy(() => import('./routes/SalaisuusSivu'));

/** Perustietojen hallinta yhdellä oikeudella — toistuu neljällä reitillä. */
function Perustiedot({ children }: { children: React.ReactNode }) {
  return <VaatiiOikeuden oikeus="canManageMasterData">{children}</VaatiiOikeuden>;
}

export default function App() {
  return (
    <Suspense fallback={<Latautuu />}>
      <Routes>
        <Route path="/kirjaudu" element={<KirjauduSivu />} />
        {/* Jaetun salaisuuden avaaminen ei vaadi kirjautumista: vastaanottaja
          on asiakas, jolla ei ole tunnusta (docs/SALAISUUDET.md). */}
        <Route path="/s/:id" element={<SalaisuusSivu />} />

        <Route
          element={
            <VaatiiKirjautumisen>
              <AppShell />
            </VaatiiKirjautumisen>
          }
        >
          <Route path="/" element={<Navigate to="/kalenteri" replace />} />
          <Route path="/kalenteri" element={<KalenteriSivu />} />

          <Route path="/omat-tunnit" element={<OmatTunnitSivu />} />

          {/* Agenttitunnus on henkilökohtainen apuväline, ei hallinnan
            asetus (backlog 5). Portti on sama kuin kirjaamisella: tunnus ei
            voi tehdä mitään, mitä sen luoja ei itse saisi tehdä. */}
          <Route
            path="/agentti"
            element={
              <VaatiiOikeuden oikeus="canLogTime">
                <AgenttiSivu />
              </VaatiiOikeuden>
            }
          />

          {/* Ulkoasu on jokaisen omat asetukset — teema, kieli ja värit ovat
            sen ihmisen valintoja, joka ruutua katsoo. Siksi reitti on
            päätasolla eikä hallinnassa: tavallinen kirjaaja ei pääse
            hallintaan lainkaan. Sivun ainoa koko taloa koskeva osio,
            yrityksen tunnus, on portin takana sivun sisällä. */}
          <Route path="/ulkoasu" element={<UlkoasuSivu />} />
          <Route
            path="/salaisuudet"
            element={
              <VaatiiOikeuden oikeus="canShareSecrets">
                <SalaisuudetSivu />
              </VaatiiOikeuden>
            }
          />

          {/* Vanha osoite oli hallinnan välilehti. Kirjanmerkit ja
            ohjeisiin kirjoitetut polut eivät saa kuolla, ja uudelleenohjaus
            on päätasolla, koska hallinnan portti hylkäisi juuri ne
            käyttäjät, joita siirto varten tehtiin. */}
          <Route path="/hallinta/ulkoasu" element={<Navigate to="/ulkoasu" replace />} />

          {/* Koulutukset ovat auki jokaiselle kirjautuneelle, myös
            raportoijalle: tietosuojakoulutus koskee kaikkia, jotka näkevät
            järjestelmässä toisen ihmisen tietoja (docs/KOULUTUKSET.md). */}
          <Route path="/koulutukset" element={<KoulutuksetSivu />} />
          <Route path="/koulutukset/:koodi" element={<KoulutusSivu />} />
          <Route path="/koulutukset/:koodi/materiaali/:osio" element={<KoulutusOsioSivu />} />
          <Route path="/koulutukset/:koodi/tentti/:id" element={<KoulutusTenttiSivu />} />
          <Route path="/koulutukset/:koodi/tulos/:id" element={<KoulutusTulosSivu />} />

          {/* Alaisen sivun pääsyn ratkaisee palvelin esihenkilösuhteesta
            (canViewDevelopment); reitti on auki jokaiselle, jolla moduuli
            on. Piilottaminen olisi käytettävyyttä, ei turvaa — eikä sitä
            voi rakentaa tietoon, jota selaimella ei ole. */}
          <Route
            path="/kehitys"
            element={
              <VaatiiOikeuden oikeus="canUseDevelopment">
                <KehitysSivu />
              </VaatiiOikeuden>
            }
          />
          {/* Polkureitit ennen `/kehitys/:id`:tä: React Router valitsee
            tarkimman osuman, mutta järjestys tekee säännöstä luettavan —
            "polut" ei ole kenenkään tunniste. */}
          <Route
            path="/kehitys/polut"
            element={
              <VaatiiOikeuden oikeus="canUseDevelopment">
                <KehitysPolutSivu />
              </VaatiiOikeuden>
            }
          />
          <Route
            path="/kehitys/polut/:koodi"
            element={
              <VaatiiOikeuden oikeus="canUseDevelopment">
                <KehitysPolkuSivu />
              </VaatiiOikeuden>
            }
          />
          <Route
            path="/kehitys/:id"
            element={
              <VaatiiOikeuden oikeus="canUseDevelopment">
                <KehitysHenkiloSivu />
              </VaatiiOikeuden>
            }
          />
          <Route
            path="/kehitys/:userId/keskustelu/:id"
            element={
              <VaatiiOikeuden oikeus="canUseDevelopment">
                <KeskusteluSivu />
              </VaatiiOikeuden>
            }
          />

          <Route
            path="/raportit"
            element={
              <VaatiiOikeuden oikeus="canViewReports">
                <RaportitSivu />
              </VaatiiOikeuden>
            }
          />
          <Route
            path="/raportit/:id"
            element={
              <VaatiiOikeuden oikeus="canViewReports">
                <RaporttiAsiakasSivu />
              </VaatiiOikeuden>
            }
          />

          {/* Projektinhallinnan taulut (docs/PROJEKTINHALLINTA.md). Portti
            on rooli; se, mitkä taulut näkyvät, on jäsenyyden asia ja
            ratkaistaan palvelimella. */}
          <Route
            path="/taulut"
            element={
              <VaatiiOikeuden oikeus="canUseBoards">
                <TaulutSivu />
              </VaatiiOikeuden>
            }
          />
          <Route
            path="/taulut/asiakas/:id"
            element={
              <VaatiiOikeuden oikeus="canUseBoards">
                <TauluSivu laji="asiakas" />
              </VaatiiOikeuden>
            }
          />
          <Route
            path="/taulut/projekti/:id"
            element={
              <VaatiiOikeuden oikeus="canUseBoards">
                <TauluSivu laji="projekti" />
              </VaatiiOikeuden>
            }
          />
          <Route
            path="/taulut/omat"
            element={
              <VaatiiOikeuden oikeus="canUseBoards">
                <OmatTehtavatSivu />
              </VaatiiOikeuden>
            }
          />
          <Route
            path="/taulut/gantt"
            element={
              <VaatiiOikeuden oikeus="canModerateBoards">
                <GanttSivu />
              </VaatiiOikeuden>
            }
          />
          <Route
            path="/taulut/kumppanit"
            element={
              <VaatiiOikeuden oikeus="canUseBoards">
                <KumppanitSivu />
              </VaatiiOikeuden>
            }
          />
          <Route
            path="/taulut/jumissa"
            element={
              <VaatiiOikeuden oikeus="canModerateBoards">
                <JumissaSivu />
              </VaatiiOikeuden>
            }
          />
          <Route
            path="/taulut/uimarata/:id/raportti"
            element={
              <VaatiiOikeuden oikeus="canUseBoards">
                <UimarataRaporttiSivu />
              </VaatiiOikeuden>
            }
          />
          <Route
            path="/taulut/tehtava/:id"
            element={
              <VaatiiOikeuden oikeus="canUseBoards">
                <TehtavaLinkkiSivu />
              </VaatiiOikeuden>
            }
          />

          <Route
            path="/vienti"
            element={
              <VaatiiOikeuden oikeus="canExport">
                <VientiSivu />
              </VaatiiOikeuden>
            }
          />

          {/* Kuukausinäkymä on luettavissa myös raportoijille (6.5), joten
            kehyksen portti on väljempi kuin sen välilehtien. */}
          <Route
            path="/hallinta"
            element={
              <VaatiiOikeuden
                oikeus={[
                  'canManageMasterData',
                  'canViewReports',
                  'canManageTrainings',
                  'canViewTrainingReports',
                ]}
              >
                <HallintaKehys />
              </VaatiiOikeuden>
            }
          >
            <Route index element={<Navigate to="/hallinta/asiakkaat" replace />} />
            <Route
              path="asiakkaat"
              element={
                <Perustiedot>
                  <AsiakkaatSivu />
                </Perustiedot>
              }
            />
            <Route
              path="asiakkaat/:id"
              element={
                <Perustiedot>
                  <AsiakasSivu />
                </Perustiedot>
              }
            />
            <Route
              path="projektit"
              element={
                <Perustiedot>
                  <ProjektitSivu />
                </Perustiedot>
              }
            />
            <Route
              path="projektit/:id"
              element={
                <Perustiedot>
                  <ProjektiSivu />
                </Perustiedot>
              }
            />
            <Route
              path="hinnasto"
              element={
                <Perustiedot>
                  <HinnastoSivu />
                </Perustiedot>
              }
            />
            <Route
              path="attribuutit"
              element={
                <Perustiedot>
                  <AttribuutitSivu />
                </Perustiedot>
              }
            />
            <Route
              path="muutosloki"
              element={
                <VaatiiOikeuden oikeus="canManageUsers">
                  <MuutoslokiSivu />
                </VaatiiOikeuden>
              }
            />
            <Route
              path="kayttajat"
              element={
                <VaatiiOikeuden oikeus="canManageUsers">
                  <KayttajatSivu />
                </VaatiiOikeuden>
              }
            />
            <Route
              path="koulutukset"
              element={
                <VaatiiOikeuden oikeus="canManageTrainings">
                  <HallintaKoulutuksetSivu />
                </VaatiiOikeuden>
              }
            />
            <Route
              path="koulutustilanne"
              element={
                <VaatiiOikeuden oikeus="canViewTrainingReports">
                  <KoulutusRaporttiSivu />
                </VaatiiOikeuden>
              }
            />
            <Route
              path="osaaminen"
              element={
                <VaatiiOikeuden oikeus="canManageCertifications">
                  <OsaaminenSivu />
                </VaatiiOikeuden>
              }
            />
            <Route
              path="kehitys"
              element={
                <VaatiiOikeuden oikeus="canViewDevelopmentOverview">
                  <KehitysKatsausSivu />
                </VaatiiOikeuden>
              }
            />
            <Route
              path="kuukaudet"
              element={
                <VaatiiOikeuden oikeus="canViewReports">
                  <KuukaudetSivu />
                </VaatiiOikeuden>
              }
            />

            {/* Järjestelmäasetukset koskevat kaikkia käyttäjiä, joten ne
              ovat tiukemman portin takana kuin jokaisen oma ulkoasu. */}
            <Route
              path="asetukset"
              element={
                <VaatiiOikeuden oikeus="canManageSettings">
                  <AsetuksetSivu />
                </VaatiiOikeuden>
              }
            />
          </Route>

          <Route path="*" element={<EiLoytynytSivu />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

function Latautuu() {
  const { t } = useKaannos();
  return (
    <div className="flex min-h-dvh items-center justify-center">
      <p className="text-teksti-vaimea text-sm">{t('yleinen.ladataan')}</p>
    </div>
  );
}
